import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { GameState, Player, ScoreMap } from "../../../shared/types";

import requestFrame from "../../../shared/request-frame.js";

import * as scoreUtil from "core/src/core/score/score-util.js";
import * as gsf from "core/src/core/game-state-functions.js";
import { wormColors } from "core/src/core/constants.js";
import { flagUrl, isFlagCode } from "../../../shared/flags";
import { useIsCompact } from "../../../shared/useResponsive";
import { ScoreFitContext, ScoreStripesContext } from "../../../shared/score-fit";
import { introSchedule, introLeaders } from "../../../shared/round-intro";
import { isTieBreak } from "../../../shared/tie-break";
import { keyLabel } from "../../../shared/key-label";
import { FlagIntroFx, RowIntroFx } from "./score-intro";
import { asset } from "../../../shared/portal-env";

// While a round is played the totals and bars follow the preliminary score
// (the gain shows as a brighter band), but the ORDER holds at the round start
// so the table doesn't churn. When the round ends the standings settle: the
// bars take the settled total, then the rows move into their final order in
// steps, top to bottom: each step lifts the row that belongs at the next rank
// into place and shifts the rows it displaces along with it, as one move; the
// next step starts when that move has finished. Timed to fit inside the
// round-over countdown.
const SETTLE_FILL_MS = 500;
const SETTLE_SWAP_MS = 1400;
const SWAP_MOVE_MS = 450;     // one step, shrunk when there are many steps
const SWAP_BUDGET_MS = 2800;  // the whole swap, so it ends before the countdown does
const ONE_MOVE_MS = 1100;     // the one-move sort (oneMoveSort) ...
const ONE_MOVE_AFTER_MS = 80; // ... right after the round ends (a frame for the totals to take)

// The orders the table passes through on its way from `from` to `to` (both
// arrays of player ids, best first): the start order, then one order per rank
// whose row was not already in place. Ranks already right add no step.
function swapSteps(from: string[], to: string[]): string[][] {
  const steps = [from];
  let cur = from;
  for (let k = 0; k < to.length; k++) {
    if (cur[k] === to[k]) continue;
    const next = cur.filter((id) => id !== to[k]);
    next.splice(k, 0, to[k]);
    steps.push(next);
    cur = next;
  }
  return steps;
}

interface Props {
  gameState: GameState;
  players: Player[];
  renderTime?: number | (() => number);
  startScore?: ScoreMap;
  maxScore?: number;
  youIds?: string[];
  // How each of your seats is steered: its two keys, or null when it isn't on
  // keys (touch). Marks the row with key chips or a dot in the worm colour.
  youKeys?: Record<string, { left: string | null; right: string | null } | null>;
  showTrophys?: boolean;
  markEliminated?: boolean;
  // Online: the server's authoritative accumulated score, set once a round is over.
  // When present it is the settled total the standings animate to.
  finalScore?: ScoreMap;
  // the settle's sort as one smooth move of every row, not step by step (a big
  // field, the Royale's: thirty rows stepping looked restless)
  oneMoveSort?: boolean;
  // Rounds-mode match: maxScore is a sentinel then, not a target, so the bars
  // scale to the leader instead of to the target.
  maxRounds?: number;
  // The match is in a tie-break (shared top past the target / the rounds):
  // the tied rows wear the gold TIE tag once the standings settle. Screens
  // that know the rounds played pass this; otherwise the score-mode check
  // below stands in.
  tieBreak?: boolean;
  // The round has ended: settle the standings from the round's start to its
  // final (bars fill, then rows swap). The round-over screen mounts with this
  // set; online it flips on in place when the server ends the round.
  roundOver?: boolean;
  // Follow the render clock instead of freezing at the round start (replay,
  // where scrubbing wants the standings at that moment).
  live?: boolean;
  // Live rounds: play the round-start roll call on the rows (round-intro.ts),
  // each row lighting up on its player's beat during the countdown.
  roundIntro?: boolean;
  // Render just this player's row (ranked against everyone): the strip each
  // of two players on one phone gets at their own end of the screen.
  only?: string;
}

// Row metrics for the roomy scoreboard, by roster size: a Brawl gets tall rows
// with big flags, a 25-player Royale still fits the rail without scrolling.
// The compact layout (phones, tablets) has one slim size whatever the roster:
// the rows that fit the room show, the rest are clipped (see fitRows below).
const PHONE_ROW = { h: 26, flag: 18, name: 13, total: 13, gap: 4 };
// The stripes (a wide map on a computer, see ScoreStripesContext): up to 7
// players each get a card, the cards sharing the board's width (5 fill it, 6
// or 7 each get a little less, fewer than 5 keep the 5-card size, centred).
// From 8 players the top 5 are cards and the rest small chips in rows below,
// fighting for a place in the top 5. Cards and chips lean like racing
// stripes; like the rows, they are placed by rank with a transform, so the
// round's settle animates them.
const STRIPE_SKEW_DEG = 22;
const CARD = { h: 72, gap: 14, flag: 30, name: 18, total: 22 };
const CHIP = { w: 164, h: 28, gapX: 8, gapY: 6, top: 10 };
const MAX_ALL_CARDS = 7;
const TOP_CARDS = 5;
const skewRun = (h: number) => h * Math.tan((STRIPE_SKEW_DEG * Math.PI) / 180);
// The stripes' last measured width. The round-over screen and the next round
// mount a fresh board; starting from this (and measuring before the first
// paint) keeps the cards from blinking out and back at every round change.
let lastStripesW = 0;

function rowMetrics(n: number, compact: boolean) {
  if (compact) return PHONE_ROW;
  if (n <= 8) return { h: 48, flag: 30, name: 17, total: 17, gap: 6 };
  if (n <= 12) return { h: 40, flag: 26, name: 16, total: 15, gap: 5 };
  if (n <= 16) return { h: 34, flag: 22, name: 15, total: 14, gap: 4 };
  return { h: 30, flag: 20, name: 14, total: 13, gap: 4 };
}

export default function Score(props: Props) {
  const { gameState, players, startScore, maxScore } = props;
  const compact = useIsCompact();
  // Compact: the height the board may take (null: no limit).
  const fit = useContext(ScoreFitContext);
  // Racing stripes along the bottom instead of rows (a wide map on a computer).
  const stripes = useContext(ScoreStripesContext) && !compact && !props.only;
  const stripesRef = useRef<HTMLDivElement>(null);
  const [stripesW, setStripesW] = useState(lastStripesW);
  useLayoutEffect(() => {
    const el = stripesRef.current;
    if (!stripes || !el) return;
    const set = (w: number) => {
      lastStripesW = w;
      setStripesW((prev) => (prev === w ? prev : w));
    };
    set(Math.floor(el.getBoundingClientRect().width));
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => set(Math.floor(entries[0].contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [stripes]);

  const [, setTick] = useState(0);
  const forceRender = () => setTick((t) => t + 1);

  // The animation loop and getRenderTime read the latest props via a ref.
  const propsRef = useRef(props);
  propsRef.current = props;

  const gameEventIndexRef = useRef(0);
  const prevRenderTimeRef = useRef(0);

  function getRenderTime() {
    const p = propsRef.current;
    let renderTime = p.renderTime;
    if (typeof renderTime === "function") {
      renderTime = renderTime();
    } else if (renderTime === undefined) {
      renderTime = p.gameState.gameTime;
    }
    return renderTime;
  }

  // Advance through game events as render time progresses; re-render when a worm
  // dies or time rewinds (replay scrub).
  useEffect(() => {
    gameEventIndexRef.current = 0;
    prevRenderTimeRef.current = 0;
    let requestId: number;
    function update() {
      const gs = propsRef.current.gameState;
      const renderTime = getRenderTime();
      let shouldUpdate = false;
      if (renderTime < prevRenderTimeRef.current) {
        gameEventIndexRef.current = 0;
        shouldUpdate = true;
      }
      while (gameEventIndexRef.current < gs.gameEvents.length) {
        const gameEvent = gs.gameEvents[gameEventIndexRef.current];
        if (gameEvent.time <= renderTime) {
          if (gameEvent.type === "player_died") {
            shouldUpdate = true;
          }
          gameEventIndexRef.current++;
        } else {
          break;
        }
      }
      if (shouldUpdate) {
        // setTimeout so the re-render isn't synchronous inside the frame callback.
        setTimeout(forceRender, 0);
      }
      requestId = requestFrame(update);
      prevRenderTimeRef.current = renderTime;
    }
    requestId = requestFrame(update);
    return () => window.cancelAnimationFrame(requestId);
  }, []);

  // Reset the event cursor whenever the gameState object is swapped (new round).
  const prevGameStateRef = useRef(gameState);
  useEffect(() => {
    if (prevGameStateRef.current !== gameState) {
      prevGameStateRef.current = gameState;
      gameEventIndexRef.current = 0;
      prevRenderTimeRef.current = 0;
      forceRender();
    }
  }, [gameState]);

  // The roll call: (re)armed whenever a round starts while its countdown is
  // still running. introAt is the render clock at that moment; the rows' CSS
  // animations are delayed relative to it so they land on the board's beats.
  const [intro, setIntro] = useState<{ key: number; at: number } | null>(null);
  useEffect(() => {
    if (!props.roundIntro) return;
    const t = getRenderTime();
    if (t < (gameState as any).startPhaseTime) setIntro((prev) => ({ key: (prev ? prev.key : 0) + 1, at: t }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, props.roundIntro]);

  // Settle stage: 0 = round-start order, live totals; 1 = settled totals;
  // 2 = the swap runs, one step per swapStep. Driven by roundOver.
  const [stage, setStage] = useState(0);
  const [swapStep, setSwapStep] = useState(0);
  // The swap's step count and pace, as of the render that started it.
  const swapPlanRef = useRef({ steps: 1, moveMs: SWAP_MOVE_MS });
  useEffect(() => {
    if (!props.roundOver) {
      setStage(0);
      setSwapStep(0);
      return;
    }
    // (the Royale's one-move sort goes at once: the board is done before the card has played in)
    const a = setTimeout(() => setStage(1), props.oneMoveSort ? 0 : SETTLE_FILL_MS);
    const b = setTimeout(() => setStage(2), props.oneMoveSort ? ONE_MOVE_AFTER_MS : SETTLE_SWAP_MS);
    return () => {
      clearTimeout(a);
      clearTimeout(b);
    };
  }, [props.roundOver]);
  useEffect(() => {
    if (stage < 2) return;
    // One step per move: the next starts when the previous move has finished.
    let i = 0;
    const { steps, moveMs } = swapPlanRef.current;
    const id = setInterval(() => {
      i++;
      setSwapStep(i);
      if (i >= steps - 1) clearInterval(id);
    }, moveMs);
    return () => clearInterval(id);
  }, [stage]);

  const roundScore = scoreUtil.calculateRoundScore(gameState, getRenderTime());
  const zeros: ScoreMap = {};
  players.forEach((p) => {
    zeros[p.id] = 0;
  });
  const startMap = scoreUtil.combineScores(zeros, startScore || {}) as ScoreMap;
  const liveMap = scoreUtil.combineScores(startMap, roundScore) as ScoreMap;
  const finalMap = (props.finalScore ? scoreUtil.combineScores(zeros, props.finalScore) : liveMap) as ScoreMap;

  // What the rows show (totals, gains) and the order they are ranked by.
  let shownMap: ScoreMap;
  let orderMap: ScoreMap;
  if (props.live) {
    shownMap = liveMap;
    orderMap = liveMap;
  } else {
    shownMap = stage >= 1 ? finalMap : liveMap;
    orderMap = stage >= 2 ? finalMap : startMap;
  }
  // Best first; level on points, whoever scored more this round is higher
  // (the Royale's cut breaks ties the same way).
  const sortedOf = (map: ScoreMap) => scoreUtil.createSortedList(map).sort((a: any, b: any) =>
    b.score - a.score || (b.score - (startMap[b.id] || 0)) - (a.score - (startMap[a.id] || 0)));
  const sortedScoreList = sortedOf(orderMap);

  // The order the rows are placed in right now: the swap walks from the
  // round-start order to the final one in steps (see swapSteps).
  const idsOf = (map: ScoreMap) => sortedOf(map).map((ps: any) => ps.id as string);
  let placedIds: string[];
  if (!props.live && stage >= 2) {
    const from = idsOf(startMap), to = idsOf(finalMap);
    const steps = props.oneMoveSort ? (from.join() === to.join() ? [from] : [from, to]) : swapSteps(from, to);
    swapPlanRef.current = { steps: steps.length, moveMs: props.oneMoveSort ? ONE_MOVE_MS : Math.min(SWAP_MOVE_MS, Math.floor(SWAP_BUDGET_MS / Math.max(1, steps.length - 1))) };
    placedIds = steps[Math.min(swapStep, steps.length - 1)];
  } else {
    placedIds = sortedScoreList.map((ps: any) => ps.id as string);
  }
  const settled = props.live || (stage >= 2 && swapStep >= swapPlanRef.current.steps - 1);
  const schedule = intro ? introSchedule(players, startMap, gameState) : null;
  const delays = schedule ? schedule.delays : null;
  // The leader's row lights up in gold, and its edge stays lit through the roll
  // call, going out with the name tags as the countdown starts.
  const introLeaderIds = intro ? introLeaders(players, startMap) : [];
  // The edge goes out with the name tags on the board.
  const edgeOffAt = schedule ? schedule.fadeAt + schedule.fadeDur : 0;

  const youIds = props.youIds || [];
  // The mark on your own row: your two keys when you steer with keys. On
  // touch there are no keys to show, and the row's edge in your worm colour
  // already says it is yours, so nothing else is added.
  const youMark = (id: string) => {
    const keys = props.youKeys ? props.youKeys[id] : null;
    if (keys && (keys.left || keys.right)) {
      return (
        <span className="score-you-keys" aria-label="your keys">
          <kbd className="score-key">{keyLabel(keys.left)}</kbd>
          <kbd className="score-key">{keyLabel(keys.right)}</kbd>
        </span>
      );
    }
    return null;
  };
  const aliveIds: Record<string, boolean> = {};
  gsf.getAlivePlayers(gameState).forEach((p: any) => {
    aliveIds[p.id] = true;
  });

  function isTie() {
    if (props.tieBreak !== undefined) return props.tieBreak;
    return isTieBreak(orderMap, maxScore, props.maxRounds);
  }

  // The bars measure toward the score target. A score past the target (the
  // tie-break rounds, or a big last round) stretches the scale to the leader
  // so no bar clips; every bar rescales with it. In rounds mode there is no
  // target, so they scale to the leader (relative standing only).
  const hasTarget = !!maxScore && !props.maxRounds;
  let leader = 0;
  players.forEach((p) => {
    leader = Math.max(leader, shownMap[p.id] || 0);
  });
  const scaleMax = Math.max(1, hasTarget ? Math.max(maxScore as number, leader) : leader);
  const m = rowMetrics(players.length, compact);
  const pitch = m.h + m.gap;

  // Rank per player from the placed order; placements for trophies (ties share).
  const rankOf: Record<string, number> = {};
  placedIds.forEach((id, i) => {
    rankOf[id] = i;
  });

  // The row budget (compact): as many rows as the measured room holds, at
  // least one. Your own rows always show: any of them ranked below the budget
  // take the last slots, in rank order, and the rows they displace are
  // clipped. So the top of the table is always the true top, and you can
  // always find yourself at the bottom of it (its rank number says where you
  // really are).
  const rows_n = props.only ? 1 : compact && fit !== null ? Math.max(1, Math.min(players.length, Math.floor((fit + m.gap) / pitch))) : players.length;
  const youRanks = youIds.map((id) => rankOf[id]).filter((r) => r !== undefined).sort((a, b) => a - b);
  let jumped = 0; // your rows ranked outside the budget's top
  for (;;) {
    const next = youRanks.filter((r) => r >= rows_n - jumped).length;
    if (next === jumped) break;
    jumped = next;
  }
  const topSlots = rows_n - jumped;
  const jumpedIds = youRanks.filter((r) => r >= topSlots).map((r) => placedIds[r]);
  // The slot a row is placed in, or -1 when it is clipped away.
  const slotOf = (id: string) => {
    if (props.only) return id === props.only ? 0 : -1;
    const rank = rankOf[id] ?? players.length - 1;
    if (rank < topSlots) return rank;
    const j = jumpedIds.indexOf(id);
    return j === -1 ? -1 : topSlots + j;
  };

  // Stripes: where each rank sits (x, y in px) and the cards' width.
  const n = players.length;
  const allCards = n <= MAX_ALL_CARDS;
  const cardCount = allCards ? n : TOP_CARDS;
  const slots = allCards ? Math.max(TOP_CARDS, n) : TOP_CARDS;
  const cardRun = skewRun(CARD.h);
  const cardW = Math.max(60, (stripesW - cardRun - (slots - 1) * CARD.gap) / slots);
  const cardsOffset = cardRun / 2 + (allCards && n < TOP_CARDS ? ((TOP_CARDS - n) * (cardW + CARD.gap)) / 2 : 0);
  const chipCount = n - cardCount;
  const chipRun = skewRun(CHIP.h);
  // As many rows as the width needs, then the chips shared evenly between them:
  // 25 chips where 8 fit are 7, 7, 7, 4, never 8, 8, 8 and one left on its own.
  const chipsFit = Math.max(1, Math.floor((stripesW - chipRun + CHIP.gapX) / (CHIP.w + CHIP.gapX)));
  const chipRows = chipCount > 0 ? Math.ceil(chipCount / chipsFit) : 0;
  const chipsPerRow = chipRows > 0 ? Math.ceil(chipCount / chipRows) : chipsFit;
  const stripePos = (rank: number) => {
    if (rank < cardCount) return { x: cardsOffset + rank * (cardW + CARD.gap), y: 0, card: true };
    const j = rank - cardCount;
    const row = Math.floor(j / chipsPerRow);
    const inRow = row < chipRows - 1 ? chipsPerRow : chipCount - row * chipsPerRow;
    const rowW = inRow * (CHIP.w + CHIP.gapX) - CHIP.gapX;
    const x0 = (stripesW - rowW) / 2;
    return { x: x0 + (j % chipsPerRow) * (CHIP.w + CHIP.gapX), y: CARD.h + CHIP.top + row * (CHIP.h + CHIP.gapY), card: false };
  };
  // (Until the width is known nothing is drawn, and the height must not be
  // worked out from a width of 0: that stacks every chip in a row of its own,
  // a board taller than the screen, which squeezes the arena to nothing, which
  // keeps the width at 0. A big roster got stuck there for seconds.)
  const stripesH = CARD.h + (chipRows && stripesW > 0 ? CHIP.top + chipRows * (CHIP.h + CHIP.gapY) - CHIP.gapY : 0);

  const listStyle = {
    "--row-h": `${m.h}px`,
    "--row-flag": `${m.flag}px`,
    "--row-name": `${m.name}px`,
    "--row-total": `${m.total}px`,
    "--row-gap": `${m.gap}px`,
    height: `${rows_n * pitch - m.gap}px`,
  } as React.CSSProperties;
  const placementOf: Record<string, number> = {};
  let equalScoreCounter = 0;
  let prevScore = -1;
  sortedScoreList.forEach((ps: any, i: number) => {
    if (prevScore === ps.score) {
      equalScoreCounter++;
    } else {
      equalScoreCounter = 0;
    }
    prevScore = ps.score;
    placementOf[ps.id] = i + 1 - equalScoreCounter;
  });

  // Rows stay in roster order in the DOM (stable keys, no remounts); their rank
  // only moves them with a transform, which is what animates the swap.
  const rows = players.filter((player) => !props.only || player.id === props.only).map((player) => {
    const color = wormColors[player.colorId];
    const isYou = youIds.indexOf(player.id) !== -1;
    const total = shownMap[player.id] || 0;
    const gain = total - (startMap[player.id] || 0);
    const rank = rankOf[player.id] ?? players.length - 1;
    const slot = slotOf(player.id);
    const clipped = slot === -1;
    const jumpedRow = slot !== -1 && slot !== rank;

    let trophyElement = null;
    if (props.showTrophys && settled) {
      const placement = placementOf[player.id];
      if (placement === 1) {
        trophyElement = <img className="score-bar-trophy" src={asset("/svg/gold-trophy.svg")} alt="1st" />;
      } else if (placement === 2) {
        trophyElement = <img className="score-bar-trophy" src={asset("/svg/silver-trophy.svg")} alt="2nd" />;
      } else if (placement === 3) {
        trophyElement = <img className="score-bar-trophy" src={asset("/svg/bronze-trophy.svg")} alt="3rd" />;
      }
    }

    // (a Respawn round puts nobody out: a crash is two seconds away from the board)
    const isOut = props.markEliminated && !aliveIds[player.id] && !(gameState as any).respawn;
    // The roll call on this row: delayed to its player's beat (see round-intro.ts).
    const introStyle = delays && intro && delays[player.id] !== undefined ? ({ animationDelay: `${delays[player.id] - intro.at}s` } as React.CSSProperties) : undefined;
    const pct = Math.max(0, Math.min(100, (total / scaleMax) * 100));
    const prevPct = Math.max(0, Math.min(pct, ((total - gain) / scaleMax) * 100));
    const tie = settled && !trophyElement && isTie() && total === sortedScoreList[0].score;

    const sp = stripes ? stripePos(rank) : null;
    // The roll call (score-intro.tsx): the leader's lights up in gold.
    const lead = introLeaderIds.indexOf(player.id) !== -1;
    const fx = lead ? "#f5c542" : color;
    return (
      <div
        key={player.id}
        className={"score-bar-row" + (isOut ? " score-out" : "") + (jumpedRow ? " score-bar-jumped" : "") + (sp ? (sp.card ? " score-card" : " score-chip") : "")}
        style={{
          // the seats on this device: a faint edge in their own worm colour
          boxShadow: isYou ? `inset 0 0 0 2px ${color}${sp ? "cc" : "59"}` : undefined,
          // clipped rows park below the list's clip edge; stripes sit at their
          // rank's card or chip, leaning like a racing stripe
          transform: sp
            ? `translate(${sp.x}px, ${sp.y}px) skewX(-${STRIPE_SKEW_DEG}deg)`
            : `translateY(${(clipped ? rows_n : slot) * pitch}px)`,
          width: sp ? `${sp.card ? cardW : CHIP.w}px` : undefined,
          height: sp ? `${sp.card ? CARD.h : CHIP.h}px` : undefined,
          visibility: clipped && !sp ? "hidden" : undefined,
          // the settle's swap: every row affected by a step moves in that one step
          transitionDuration: !props.live && stage >= 2 ? `${swapPlanRef.current.moveMs}ms` : undefined,
          // (one long move: eased at both ends, so the whole field glides as one)
          transitionTimingFunction: !props.live && stage >= 2 && props.oneMoveSort ? "cubic-bezier(0.45, 0, 0.2, 1)" : undefined,
        }}
      >
        <div className="score-bar-fill" style={{ width: `${pct}%`, background: color }} />
        <div className="score-bar-gain" style={{ left: `${prevPct}%`, width: `${pct - prevPct}%`, background: color }} />
        <div className="score-bar-edge" style={{ width: `${pct}%`, borderColor: color, opacity: pct > 0 ? 1 : 0 }} />
        {introStyle && (
          <React.Fragment key={intro!.key}>
            <RowIntroFx style={introStyle} fx={fx} />
            {lead && (
              <div
                className="score-bar-intro-edge"
                style={{ ...introStyle, animationDuration: `${Math.max(0.5, edgeOffAt - (delays![player.id] || 0))}s` }}
              />
            )}
          </React.Fragment>
        )}
        <div className="score-bar-body" style={sp ? { transform: `skewX(${STRIPE_SKEW_DEG}deg)` } : undefined}>
          {compact && <span className="score-bar-pos">{rank + 1}</span>}
          {/* The flag stays put through the roll call (the ring, wash and sweep
              are the row's intro); a re-mount here made it blink out and back
              between rounds. */}
          <span className="score-flag-wrap">
            <span className="score-bar-flag" style={{ boxShadow: `0 0 0 2px ${color}`, background: color }}>
              {isFlagCode(player.flag) ? <img src={flagUrl(player.flag!)} alt="" /> : null}
            </span>
            {introStyle && <FlagIntroFx key={intro!.key} style={introStyle} fx={fx} />}
          </span>
          <span className="score-bar-name">{player.name}</span>
          {isYou ? youMark(player.id) : null}
          {trophyElement}
          {tie && <span className="score-bar-tie">TIE</span>}
          {sp ? (
            // a card or chip: the round's points just left of the total
            <>
              <span className="score-bar-delta score-card-delta" style={{ opacity: gain > 0 ? 1 : 0 }}>+{gain}</span>
              <span className="score-bar-total">{total}</span>
            </>
          ) : (
            <>
              <span className="score-bar-delta" style={{ opacity: gain > 0 ? 1 : 0 }}>+{gain}</span>
              <span className="score-bar-total">{total}</span>
            </>
          )}
        </div>
      </div>
    );
  });

  if (stripes) {
    const stripesStyle = {
      "--row-flag": `${CARD.flag}px`,
      "--row-name": `${CARD.name}px`,
      "--row-total": `${CARD.total}px`,
      height: `${stripesH}px`,
    } as React.CSSProperties;
    return (
      <div ref={stripesRef} className={"score-bars score-stripes" + (cardW < 260 ? " score-stripes-narrow" : "")} style={stripesStyle}>
        {stripesW > 0 ? rows : null}
      </div>
    );
  }

  return (
    <div className={"score-bars" + (compact ? " score-bars-phone" : "")} style={listStyle}>
      {rows}
    </div>
  );
}
